/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Select } from 'tea-component';
import HeatMapTable, {
  SortItem,
  HeatCellDataStruct,
  creatHeatColumns,
  HeatMapTableColumn,
} from '@/pages/dashboard/components/HeatMapTable';
import Accumulate from '@/pages/dashboard/components/Accumulate';
import MetricAndUpDown from '@/pages/dashboard/components/MetricAndUpDown';
import {
  commonValueFormatter,
  formatAccumulation,
  formatFilterValues,
  formatNum,
  formatPercentNum,
  getUpAndDownStatus,
  getWeekDay,
} from '@/pages/dashboard/utils/utils';
import { getLocale, useIntl, useSelector, shallowEqual } from 'umi';
import { DATE_FORMAT, KEY_DAYS_DEFAULT_VALUE } from '../../_config/constant';
import {
  useSelectArrObj,
  useWeekDayArr,
} from '@/pages/dashboard/utils/customHook';
import {
  downloadFileWithFileFormat,
  useNotificationOfDownloadTable,
} from '@/pages/dashboard/components/DownloadFormatModal';
import {
  LANGUAGE_MAP,
  PERIODTIMEOBJ,
} from '@/pages/dashboard/_config/constant';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import useData from './useData';
import { downLoadApi } from '@/pages/dashboard/services/common';
import { Props, TableReqParams } from './_type';
import type {
  ITableMetricConfig,
  IScenarioWhen,
  TTableMetricKey,
} from './_type';
import { dynamicString } from '@home/utils/dynamicString';
import dataCommonFormat from '@home/utils/dbDataFormat/dataCommonFormat';
import {
  resolveAndSelectName,
  prepareMetricsDataSource,
} from '@/pages/dashboard/utils/metricUtils';
import { filterComponentMetricsByTableMetric } from '../RetentionTableCo';
import { getDateFormatRangeString } from '@/pages/dashboard/components/OverallTableCo/hooks';
import './index.scss';
import { WIDTH_MAP_RETURN_TABLE } from './_constant';

/**
 * 默认场景匹配条件：配置未提供 scenarioOrder/scenarioWhen 时使用
 * 顺序：aggs 优先（周/月聚合），再 all（不勾选 by date 时，含日粒度），最后 byDate 兜底
 * - 日粒度下不展示周/月汇总选择，segmentVal 为 undefined，不勾选 by date 时用 all 与周/月汇总一致
 */
const TABLE_METRIC_SCENARIOS: Array<{
  key: TTableMetricKey;
  matches: (segmentVal?: string, hasDate?: boolean) => boolean;
}> = [
  { key: 'aggs', matches: (s) => s === 'aggs' },
  { key: 'all', matches: (_s, h) => !h },
  { key: 'byDate', matches: () => true },
];

/** 校验 scenarioWhen 条件是否匹配 */
function matchesScenarioWhen(
  when: IScenarioWhen | undefined,
  segmentVal?: string,
  hasDate?: boolean,
): boolean {
  if (!when || (when.segmentVal === undefined && when.hasDate === undefined))
    return true;
  if (when.segmentVal !== undefined && when.segmentVal !== segmentVal)
    return false;
  if (when.hasDate !== undefined && when.hasDate !== hasDate) return false;
  return true;
}

/**
 * 将 tableMetric（数组或对象）解析为当前场景下的指标数组
 * - 数组：直接返回（兼容旧配置）
 * - 对象：优先用 config.scenarioOrder + scenarioWhen，未配置则用 TABLE_METRIC_SCENARIOS
 */
function resolveTableMetric(
  tableMetric: string[] | ITableMetricConfig | undefined,
  segmentVal?: string,
  hasDate?: boolean,
): string[] {
  if (!tableMetric) return [];
  if (Array.isArray(tableMetric)) return tableMetric;
  const key = getEffectiveScenarioKey(tableMetric, segmentVal, hasDate);
  if (key === undefined)
    return (
      (tableMetric as ITableMetricConfig).byDate ??
      (tableMetric as ITableMetricConfig).all ??
      (tableMetric as ITableMetricConfig).aggs ??
      []
    );
  const val = (tableMetric as ITableMetricConfig)[key];
  return val?.length
    ? val
    : (tableMetric as ITableMetricConfig).byDate ??
        (tableMetric as ITableMetricConfig).all ??
        (tableMetric as ITableMetricConfig).aggs ??
        [];
}

/** 获取当前命中的场景 key，用于读取 scenarioExtraParams 等 */
function getEffectiveScenarioKey(
  tableMetric: string[] | ITableMetricConfig | undefined,
  segmentVal?: string,
  hasDate?: boolean,
): TTableMetricKey | undefined {
  if (!tableMetric) return undefined;
  if (Array.isArray(tableMetric)) return undefined;
  const cfg = tableMetric as ITableMetricConfig;
  const order = cfg.scenarioOrder?.length
    ? cfg.scenarioOrder
    : TABLE_METRIC_SCENARIOS.map((s) => s.key);
  const useConfigWhen =
    cfg.scenarioWhen && Object.keys(cfg.scenarioWhen).length > 0;
  for (const key of order) {
    const val = cfg[key];
    if (!val?.length) continue;
    const matches = useConfigWhen
      ? matchesScenarioWhen(cfg.scenarioWhen?.[key], segmentVal, hasDate)
      : TABLE_METRIC_SCENARIOS.find((s) => s.key === key)?.matches(
        segmentVal,
        hasDate,
      ) ?? false;
    if (matches) return key;
  }
  return undefined;
}

export default function RetentionReturnTableCo(props: Props) {
  const {
    config,
    data,
    style,
    className,
    tableMetric: propsTableMetric,
    title: propsTitle,
    titleCN: propsTitleCN,
    keyDaysConfig: propsKeyDaysConfig,
    api: propsApi,
    tableDisWidthMap: propsTableDisWidthMap,
    metricColumnWidth: propsMetricColumnWidth,
    accumulateSelectMultipleBoxClassName,
    segmentVal,
    dateOptionDisabled: propsDateOptionDisabled,
    heatColumnTitleSuffix: configHeatColumnTitleSuffix,
  } = props;
  const {
    title: configTitle,
    titleCN: configTitleCN,
    componentMetrics: configComponentMetrics,
    keyDaysConfig: configKeyDaysConfig,
    api: { tableDownloadApi: configTableDownloadApi },
    tableDisWidthMap: configTableDisWidthMap,
    metricColumnWidth: configMetricColumnWidth,
    dateOptionDisabled: configDateOptionDisabled,
  } = config;
  const gameCode = useSelector((state: any) => {
    return state?.dashboard?.currentGame?.game_code;
  }, shallowEqual);
  const configTableMetric = propsTableMetric ?? config?.tableMetric;
  // retentionColumsNum = 2 是mobile框架的默认值
  const { tag, res_tag: resTag, retentionColumsNum = '2' } = data || {};
  const metricsInfo = props?.data?.metricsInfo;

  const contextObj = useContext(FilterContext);
  const { filterLoading, filterVal = {} } = contextObj || {};
  const isEn = getLocale() === 'en-US';
  const { formatMessage } = useIntl();
  // 准备 metricsData 用于 dynamicString 解析
  const metricsData = useMemo(() => {
    return prepareMetricsDataSource(metricsInfo);
  }, [metricsInfo]);
  const dateOptionDisabled =
    propsDateOptionDisabled ?? configDateOptionDisabled ?? true;
  let SEPARATED_OPTIONS = useSelectArrObj(filterVal);
  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter(
    (i: any) => i?.groupKey === 'separated',
  );
  SEPARATED_OPTIONS.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    disabled: dateOptionDisabled,
  } as any);
  const [accumulated, setAccumulated] = useState<string[]>(['date']);
  const hasDate = accumulated?.includes('date');
  const effectiveTableMetric = useMemo(
    () => resolveTableMetric(configTableMetric, segmentVal, hasDate),
    [configTableMetric, segmentVal, hasDate],
  );
  const effectiveScenarioKey = useMemo(
    () => getEffectiveScenarioKey(configTableMetric, segmentVal, hasDate),
    [configTableMetric, segmentVal, hasDate],
  );
  const isTableMetricObject =
    configTableMetric && !Array.isArray(configTableMetric);
  const {
    title,
    titleCN,
    componentMetrics,
    keyDaysConfig,
    api,
    tableDownloadApi,
    tableDisWidthMap,
    metricColumnWidth,
  } = useMemo(() => {
    return {
      title: propsTitle || configTitle,
      titleCN: propsTitleCN || configTitleCN,
      componentMetrics: filterComponentMetricsByTableMetric(
        configComponentMetrics || [],
        effectiveTableMetric,
      ),
      keyDaysConfig: propsKeyDaysConfig || configKeyDaysConfig,
      api: propsApi || config?.api,
      tableDownloadApi: propsApi?.tableDownloadApi || configTableDownloadApi,
      tableDisWidthMap:
        propsTableDisWidthMap ||
        configTableDisWidthMap ||
        WIDTH_MAP_RETURN_TABLE,
      metricColumnWidth: propsMetricColumnWidth ?? configMetricColumnWidth,
    };
  }, [
    propsTitle,
    configTitle,
    propsTitleCN,
    configTitleCN,
    configComponentMetrics,
    effectiveTableMetric,
    propsKeyDaysConfig,
    configKeyDaysConfig,
    propsApi,
    config?.api,
    propsTableDisWidthMap,
    configTableDisWidthMap,
    propsMetricColumnWidth,
    configMetricColumnWidth,
  ]);
  // Key Days 筛选器
  const [keyDays, setKeyDays] = useState<number>(KEY_DAYS_DEFAULT_VALUE);
  // 宽表数据
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableCopyData, setTableCopyData] = useState<any[]>([]);
  // 排序值
  const [sortValue, setSortValue] = useState<SortItem[]>([]);
  const [columns, setColumns] = useState<HeatMapTableColumn[]>([
    { name: '', features: { sortable: true } },
  ]); // 默认值是为了解决控制台warning, 无功能作用
  const [headKey, setHeadKay] = useState<string[] | undefined>();
  // 表格分页记录
  const [tablePaging, setTablePaging] = useState({
    pageIndex: 1,
    pageSize: 10,
  });
  const [total, setTotal] = useState(0);
  const weekDayArr = useWeekDayArr();
  const accumulateOldValueRef = useRef(['date']);
  const extraParams = useMemo(() => {
    const cfg = isTableMetricObject
      ? (configTableMetric as ITableMetricConfig)
      : null;
    return (
      cfg?.scenarioExtraParams?.[effectiveScenarioKey as TTableMetricKey] ??
      undefined
    );
  }, [isTableMetricObject, configTableMetric, effectiveScenarioKey]);
  // tableMetric 为对象时由 effectiveTableMetric[0] 取值；否则使用 data.res_tag（需 tableMetric 对象以支持多场景）
  const effectiveResTag = useMemo(() => {
    if (isTableMetricObject && effectiveTableMetric?.[0]) {
      return effectiveTableMetric[0];
    }
    return resTag;
  }, [isTableMetricObject, effectiveTableMetric, resTag]);
  const retentionTableData = useData(
    { api },
    filterVal,
    filterLoading,
    tag || '',
    accumulated,
    effectiveResTag,
    extraParams,
  );
  const {
    getRetentionDataFunc,
    componentData,
    error,
    loading,
    overLimitBarTriggerKey,
  } = retentionTableData;
  const tableTitle = useMemo(() => {
    return isEn ? title : titleCN;
  }, [isEn, title, titleCN]);
  const { setNoticeStatus } = useNotificationOfDownloadTable(tableTitle || '');

  useEffect(() => {
    setTablePaging({ pageIndex: 1, pageSize: 10 });
    setKeyDays(KEY_DAYS_DEFAULT_VALUE);
    setSortValue([]);
  }, [filterVal]);

  // segmentVal 变化时（切换汇总/聚合粒度），重置排序到初始状态
  useEffect(() => {
    setSortValue([]);
    setTablePaging({ pageIndex: 1, pageSize: 10 });
  }, [segmentVal]);

  const getHeatColumns = (keys: string[]) => {
    // const retentionColumsNum: any = {
    //   pc: '1',
    //   mobile: '2',
    //   pc_inflexion: '1'
    // }
    return creatHeatColumns(
      keys?.map((key: string) => {
        const type = key.slice(0, 1);
        let num = key.slice(1);
        if (num === retentionColumsNum && !isEn) {
          num = '次';
        }
        // 支持动态字符串获取 title
        // 优先从 metricsInfo 中获取配置（支持模板字符串）
        const metricInfo = metricsInfo?.[key];
        let title = '';
        if (metricInfo) {
          // 从 metricsInfo 中获取 en 和 cn，支持动态字符串解析
          const enName = metricInfo?.en;
          const cnName = metricInfo?.cn;
          title = resolveAndSelectName(isEn, enName, cnName, metricsInfo) || '';
        }

        // 如果没有配置或解析失败，使用原有的 formatMessage 逻辑（兼容性）
        // 支持通过 heatColumnTitleSuffix 配置切换 locale key，如 '_new' → 'D15留存'
        if (!title) {
          const suffix = configHeatColumnTitleSuffix ?? '';
          title = formatMessage(
            {
              id: `engagement_retention_${type?.toLowerCase() ?? 'd'}${suffix}`,
            },
            { num },
          );
        }
        return {
          code: `__heat_${key}`,
          title,
        } as any;
      }),
    );
  };

  const getNormalColumns = (): HeatMapTableColumn[] => {
    // register列配置
    const metricsConfig = componentMetrics?.[0];
    const {
      enName,
      cnName,
      metricKey: metricsKeyVal,
      type,
      decimal,
      percentDecimals,
      formatRule,
      dodFormatRule,
      metricsKey: metricsConfigKey,
    } = (metricsConfig || {}) as any;
    const metricsKey = metricsConfigKey || metricsKeyVal;
    // const name = isEn ? enName : cnName
    const name =
      resolveAndSelectName(isEn, enName, cnName, metricsInfo) ||
      (isEn ? enName : cnName) ||
      '';
    const metricWidth = (() => {
      const cfg = metricColumnWidth;
      if (cfg === null || cfg === undefined)
        return accumulated.length >= 2 ? 196 : 170;
      if (typeof cfg === 'number') return cfg;
      return (isEn ? cfg.en : cfg.cn) ?? (accumulated.length >= 2 ? 196 : 170);
    })();
    const REGISTER_COLUMN: HeatMapTableColumn = {
      code: metricsKey || '',
      width: metricWidth,
      name, // todo 是否可以用一个
      title: name, // todo 是否可以用一个
      align: 'right',
      features: { sortable: true },
      render: (record: any) => {
        const { count = '', dod = '', status: initialStatus } = record || {};
        // status = status ? status : Number(dod) < 0 ? 'down' : 'up'
        // status = getUpAndDownStatus(dod, !!status);
        // const data = commonValueFormatter('percent', dod, percentDecimals)

        const status = getUpAndDownStatus(dod, !!initialStatus);
        // 格式化主值
        let metricValue: string | number = '';
        if (formatRule) {
          const resolvedRule = dynamicString(formatRule as string, metricsData);
          const formatted = dataCommonFormat({
            value: count,
            formatRule: resolvedRule,
          });
          metricValue =
            (formatted ??
              commonValueFormatter(type || 'int', count, decimal || 0)) ||
            '';
        } else {
          metricValue =
            commonValueFormatter(type || 'int', count, decimal || 0) || '';
        }

        // 环比
        let dodData: string | number = '';
        if (dodFormatRule) {
          const resolvedDodRule = dynamicString(
            dodFormatRule as string,
            metricsData,
          );
          dodData =
            (dataCommonFormat({ value: dod, formatRule: resolvedDodRule }) ??
              commonValueFormatter('percent', dod, percentDecimals || 1)) ||
            '';
        } else {
          dodData =
            commonValueFormatter('percent', dod, percentDecimals || 1) || '';
        }

        return (
          <MetricAndUpDown
            metric={metricValue}
            updown={{ data: dodData, status }}
            hasTooltip={false}
          />
        );
      },
    };
    const selectHeader: any = {
      zone: formatMessage({ id: 'common_Zone' }),
      region: formatMessage({ id: 'common_Region' }),
      country: formatMessage({ id: 'common_Country' }),
      system: formatMessage({ id: 'common_System' }),
      channel: formatMessage({ id: 'common_Channel' }),
      language: formatMessage({ id: 'common_Language' }),
      bundle: formatMessage({ id: 'common_Bundle' }),
      ua_network: formatMessage({ id: 'common_ua_network' }),
      version: formatMessage({ id: 'common_version' }),
      campaign: formatMessage({ id: 'common_campaign' }),
    };

    // accumulate列配置（排除 date，支持取消 By Date 后按其他维度展示）
    const accumulateColumns: HeatMapTableColumn[] = [];
    accumulated.forEach((val) => {
      if (val !== 'date') {
        const option = SEPARATED_OPTIONS.find(
          (opt: { value: string; }) => opt.value === val,
        );
        if (option) {
          accumulateColumns.push({
            code: val,
            width:
              tableDisWidthMap?.[val as keyof typeof tableDisWidthMap] || 156,
            align: 'left' as const,
            name: selectHeader[option?.value],
            title: selectHeader[option?.value],
            features: { sortable: true },
          });
        }
      }
    });
    const dateColumnWidth = hasDate ? 160 : 220;
    const DATE_COLUMN: HeatMapTableColumn = {
      code: 'date',
      width: dateColumnWidth,
      align: 'left' as const,
      lock: true,
      features: { sortable: true },
      name: formatMessage({ id: 'engagement_retention_date' }),
      render: (record: any) => {
        const dateVal = typeof record === 'object' ? record?.date : record;
        if (!dateVal) return <span className="table-text">-</span>;
        if (dateVal?.includes?.('~')) {
          const dateStr = getDateFormatRangeString(
            filterVal?.timeType || 'Day',
            String(dateVal).trim(),
          );
          return <span className="table-text">{dateStr}</span>;
        }
        const weekDay = getWeekDay(dateVal, weekDayArr)?.slice(1, -1);
        const isSunOrSat =
          weekDay === 'Sun' ||
          weekDay === 'Sat' ||
          weekDay === '周日' ||
          weekDay === '周六';
        return filterVal?.timeType === 'Month' ? (
          moment(dateVal).isValid() ? (
            moment(dateVal).format('YYYY-MM')
          ) : (
            dateVal
          )
        ) : (
          <>
            <span className="table-text">
              {moment(dateVal).isValid()
                ? moment(dateVal).format('YYYY-MM-DD')
                : dateVal}
            </span>
            {moment(dateVal).isValid() && (
              <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>
                {weekDay}
              </span>
            )}
          </>
        );
      },
    };
    // 正常来说metricsConfig是必定存在的
    return metricsConfig
      ? [DATE_COLUMN, ...accumulateColumns, REGISTER_COLUMN]
      : [DATE_COLUMN, ...accumulateColumns];
  };

  const heatCellColorPercent = (percentageStr: string) => {
    const floatNum = parseFloat(percentageStr);
    if (floatNum >= 0 && floatNum <= 0.1) return 0.05;
    if (floatNum > 0.1 && floatNum <= 0.2) return 0.1;
    if (floatNum > 0.2 && floatNum <= 0.3) return 0.2;
    if (floatNum > 0.3 && floatNum <= 0.4) return 0.3;
    if (floatNum > 0.4 && floatNum <= 0.5) return 0.4;
    if (floatNum > 0.5 && floatNum <= 0.6) return 0.5;
    if (floatNum > 0.6 && floatNum <= 0.7) return 0.6;
    if (floatNum > 0.7 && floatNum <= 0.8) return 0.7;
    if (floatNum > 0.8 && floatNum <= 0.9) return 0.8;
    return 0.85;
  };

  const {
    averageName,
    retentionUsersName,
    retentionRateName,
    retentionUsersFormatter,
    retentionRateFormatter,
  } = useMemo(() => {
    const usersFormatter = metricsInfo?.retention_users_count?.formatter;
    const rateFormatter = metricsInfo?.retention_rate?.formatter;
    const resolvedUsersFormatter = usersFormatter
      ? dynamicString(usersFormatter, metricsData, '')
      : '';
    const resolvedRateFormatter = rateFormatter
      ? dynamicString(rateFormatter, metricsData, '')
      : '';
    return {
      averageName:
        resolveAndSelectName(
          isEn,
          dynamicString(
            `{{metricsInfo.average_retention_users.en}}`,
            metricsData,
            '',
          ),
          dynamicString(
            `{{metricsInfo.average_retention_users.cn}}`,
            metricsData,
            '',
          ),
          metricsInfo,
        ) || formatMessage({ id: 'engagement_retention_average' }),
      retentionUsersName:
        resolveAndSelectName(
          isEn,
          dynamicString(
            `{{metricsInfo.retention_users_count.en}}`,
            metricsData,
            '',
          ),
          dynamicString(
            `{{metricsInfo.retention_users_count.cn}}`,
            metricsData,
            '',
          ),
          metricsInfo,
        ) || formatMessage({ id: 'engagement_retention_user' }),
      retentionRateName:
        resolveAndSelectName(
          isEn,
          dynamicString(`{{metricsInfo.retention_rate.en}}`, metricsData, ''),
          dynamicString(`{{metricsInfo.retention_rate.cn}}`, metricsData, ''),
          metricsInfo,
        ) || formatMessage({ id: 'engagement_retention_rate' }),
      retentionUsersFormatter: resolvedUsersFormatter,
      retentionRateFormatter: resolvedRateFormatter,
    };
  }, [isEn, metricsData, metricsInfo, formatMessage]);

  /** 返回数据格式整理 */
  const handleTableData = (data: any[] = [], isCopy?: boolean) => {
    const metricsConfig = componentMetrics?.[0];
    const { metricKey: metricsKeyVal, metricsKey: metricsConfigKey } =
      (metricsConfig || {}) as any;
    const metricsKey = metricsConfigKey || metricsKeyVal;
    const timeType = (filterVal?.timeType || 'Day') as keyof typeof DATE_FORMAT;
    return data.map(
      ({
        type,
        date: dateValue,
        count,
        dod,
        group_by_cn = [],
        group_by_en = [],
        rentention_detail = [],
        status,
      } = {}) => {
        let date: string;
        if (dateValue?.includes?.('~')) {
          date = getDateFormatRangeString(timeType, String(dateValue).trim());
        } else {
          date = moment(dateValue).isValid()
            ? moment(dateValue).format(`${DATE_FORMAT[timeType]}`)
            : dateValue ?? '-';
        }

        // 计算 D/W/M2 ~ D/W/M90数据
        const details: HeatCellDataStruct = {};
        rentention_detail.forEach(
          (detail: {
            split: (arg0: string) => [any, any, ('-' | undefined)?];
          }) => {
            const [key, percentageStr, userCount = '-'] = detail.split(':');
            const rateValue = percentageStr === '' ? '-' : percentageStr;
            // const cellContent = isCopy ? percentageStr : formatPercentNum(percentageStr)
            const cellContent = isCopy
              ? rateValue
              : retentionRateFormatter
                ? dataCommonFormat({
                  value: Number(rateValue),
                  formatRule: retentionRateFormatter,
                }) || formatPercentNum(rateValue || '0')
                : formatPercentNum(rateValue || '0');

            (details as any)[`__heat_${key}`] = {
              bubble: {
                content: (
                  <>
                    <span className="table-bubble-date">
                      {date === 'Average' ? averageName : date}
                    </span>
                    <div className="table-bubble-info">
                      <span className="Revenue-table-Lspan">
                        {retentionUsersName}
                      </span>
                      {/* <span className='Revenue-table-Rspan'>{userCount === '' ? '-' : formatNum(+userCount)}</span> */}
                      <span className="Revenue-table-Rspan">
                        {!userCount || userCount === '-'
                          ? '-'
                          : retentionUsersFormatter
                            ? dataCommonFormat({
                              value: +userCount,
                              formatRule: retentionUsersFormatter,
                            }) || formatNum(+userCount)
                            : formatNum(+userCount)}
                      </span>
                    </div>
                    <div className="table-bubble-info">
                      <span className="Revenue-table-Lspan">
                        {retentionRateName}
                      </span>
                      <span className="Revenue-table-Rspan">{cellContent}</span>
                    </div>
                  </>
                ),
                placement: 'left',
              },
              cellContent,
              colorPercent: heatCellColorPercent(
                percentageStr === '' || percentageStr === '-'
                  ? '0'
                  : percentageStr,
              ),
            };
            if (type === 'avg')
              (details as any)[`__heat_${key}`].bubble.visible = false;
          },
        );

        // 计算 Accumulate筛选器的动态列的数据（支持取消 By Date 后按其他维度展示）
        const accumulatedData: any = {};
        const groupList = accumulated?.filter((v: any) => v !== 'date') || [];
        groupList.forEach((value, i) => {
          accumulatedData[value] = !isEn
            ? group_by_cn?.[i] || '-'
            : group_by_en?.[i] || '-';
        });

        const obj: any = {
          date,
          ...accumulatedData,
          ...details,
        };
        if (metricsKey) {
          obj[metricsKey] = {
            dod,
            count: isCopy
              ? isNaN(parseFloat(count as string))
                ? count
                : parseFloat(count as string)
              : count === ''
                ? '-'
                : count,
            status,
          };
        }
        return obj;
      },
    );
  };

  useEffect(() => {
    const keys = (
      componentData?.rentention_days as any
    )?.[0]?.rentention_detail?.map((detail: any) => {
      return detail.split(':')[0];
    });
    // 表格数据最后一页的最后一行加入 avg 行
    const data = (() => {
      let data: any = componentData?.rentention_days;
      if (
        componentData?.avg?.length > 0 &&
        componentData?.rentention_days?.length > 0
      ) {
        data = [
          ...(data || []),
          {
            date: formatMessage({ id: 'engagement_retention_average' }),
            count: '',
            dod: '',
            status: 'no',
            group_by: [],
            rentention_detail: componentData?.avg,
            type: 'avg',
          },
        ];
      }
      return data;
    })();
    setHeadKay(keys);
    setColumns([...getNormalColumns(), ...getHeatColumns(keys || [])]);
    setTableData(handleTableData(data, false));
    setTableCopyData(handleTableData(data, true));
    setTotal(componentData?.total_record || 0);
  }, [componentData, isEn, metricColumnWidth]);

  const KEY_DAYS_OPTIONS = useMemo(() => {
    return keyDaysConfig?.map(
      (item: { value: number; enName: string; cnName: string; }) => {
        return {
          value: String(item?.value),
          text: isEn ? item?.enName : item?.cnName,
        };
      },
    );
  }, [isEn, keyDaysConfig]);

  useEffect(() => {
    if (headKey) {
      setColumns([...getNormalColumns(), ...getHeatColumns(headKey)]);
    }
  }, [isEn, headKey, metricColumnWidth]);
  /**
   * 获取上传参数对象
   */
  const getTableReqParams = (
    paging: { pageIndex: any; pageSize: any; },
    sort: SortItem[],
  ): TableReqParams => {
    const { rangeTime, diff, timeType, ...filters } = filterVal || {};

    let desc = true;
    const orderBy = sort.map((value) => {
      desc = value.order === 'desc';
      return value.code?.replace('__heat_', '');
    });

    const sortedBy = orderBy?.length ? orderBy?.[0] : 'date';

    return {
      game_code: gameCode || '',
      period: PERIODTIMEOBJ?.[timeType as keyof typeof PERIODTIMEOBJ],
      start_date: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
      end_date: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
      column_len: keyDays,
      retention_type: tag || 'new',
      page: paging?.pageIndex || 1,
      page_size: paging?.pageSize || 10,
      sorted_by: sortedBy,
      desc,
      filters: formatFilterValues(filters),
      accumulation: formatAccumulation(accumulated),
      ...(extraParams || {}),
    };
  };

  /**
   * 下载文件
   */
  const download = () => {
    const downloadPromise = downPromise?.();
    if (downloadPromise !== undefined && downloadPromise !== null) {
      // 有返回 Promise 对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus('loading');
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${tableTitle} ${moment().format(
            'YYYY_MM_DD HH_mm_ss',
          )}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus('success');
            },
            onDownloadError() {
              setNoticeStatus('error');
            },
          });
        } catch (e) {
          setNoticeStatus('error');
        }
      })();
    }
  };

  const downPromise = () => {
    const params: TableReqParams = getTableReqParams(tablePaging, sortValue);
    const language = LANGUAGE_MAP[getLocale()];
    return downLoadApi?.(`${tableDownloadApi}?language=${language}`, params);
  };

  return (
    <div className={`dbd-retention-table-co ${className}`} style={style}>
      <HeatMapTable
        title={tableTitle}
        timeType={filterVal?.timeType}
        records={tableData}
        copyData={tableCopyData}
        defaultColumnWidth={50}
        columns={columns}
        sorting={{
          sorts: sortValue,
          onChangeSorts: (nextSorts) => {
            setSortValue(nextSorts);
            getRetentionDataFunc(keyDays, tablePaging, nextSorts);
          },
        }}
        paging={{
          recordCount: total,
          pageSize: tablePaging.pageSize,
          pageIndex: tablePaging.pageIndex,
          onPagingChange: (pagingQuery) => {
            const nextPaging = {
              pageIndex: pagingQuery.pageIndex || 1,
              pageSize: pagingQuery.pageSize || 10,
            };
            setTablePaging(nextPaging);
            getRetentionDataFunc(keyDays, nextPaging, sortValue);
          },
          pageSizeOptions: [10, 20, 30, 50],
        }}
        loading={loading}
        error={error}
        onDownload={download}
        dataSource={metricsData as any}
        frontLogicalFlag={componentData?.front_logical_flag}
        limitCount={config?.tableConfig?.overLimitCount}
        barLimitCount={config?.tableConfig?.overLimitBarCount}
        barDownloadDisabled={config?.tableConfig?.barDownloadDisabled}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
        topTip={{
          left: (
            <>
              <Accumulate
                accumulateSelectMultipleBoxClassName={
                  accumulateSelectMultipleBoxClassName
                }
                className="register-table-select"
                value={accumulated}
                options={SEPARATED_OPTIONS}
                onChange={(value) => {
                  setAccumulated(value);
                }}
                onClose={() => {
                  if (
                    accumulateOldValueRef?.current?.toString() ===
                    accumulated?.toString()
                  ) {
                    return;
                  }
                  accumulateOldValueRef.current = accumulated;
                  // 分页和排序重置
                  setTablePaging({ pageIndex: 1, pageSize: 10 });
                  setSortValue([]);
                  getRetentionDataFunc(
                    keyDays,
                    { pageIndex: 1, pageSize: 10 },
                    [],
                  );
                }}
              />

              {filterVal?.timeType === 'Day' ? (
                <Select
                  className="register-table-select"
                  boxClassName="register-table-select-box"
                  appearance="button"
                  matchButtonWidth
                  // style={{ width: 150 }}
                  options={KEY_DAYS_OPTIONS}
                  value={String(keyDays)}
                  onChange={(value: string) => {
                    const numValue = Number(value);
                    setKeyDays(numValue);
                    getRetentionDataFunc(numValue, tablePaging, sortValue);
                  }}
                  placeholder={formatMessage({
                    id: 'engagement_retention_keydays',
                  })}
                />
              ) : (
                ''
              )}
            </>
          ),
        }}
      />
    </div>
  );
}
